// src/components/AppSidebar.js
// Shared left-side ERP-style sidebar. Used as the primary nav on every
// authenticated page (replaces the old horizontal NavBar).
//
// Organization — function-based, roughly ordered by lifecycle stage:
//   Dashboard → Inventory → Operations → PDI → Contacts → Tools → Admin
//
// Bottom bar hosts: user menu, message opener, theme toggle, logout.

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeCtx } from "../App";
import MessageWindow from "./MessageWindow";
import {
  Home, Boxes, Warehouse, Car, BadgeDollarSign, ListChecks, PlusCircle,
  Truck, CalendarDays, Users as UsersIcon, MapPin, CircleCheck, UserRoundCog,
  ClipboardList, FileText, BarChart3, Layers,
  UserPlus2Icon, LucideTractor,
  ScanQrCode, Upload, Package, Receipt,
  Settings, Sun, Moon, LogOut, MessagesSquare, Menu, X, ChevronRight,
} from "lucide-react";

// ── Navigation definition ──────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: "Dashboard",
    items: [{ to: "/home", label: "Home", icon: Home }],
  },
  {
    label: "Inventory",
    items: [
      { to: "/inventory", label: "All Records",  icon: Boxes },
      { to: "/stock",     label: "Stock",        icon: Warehouse },
      { to: "/demo",      label: "Demo Fleet",   icon: Car },
      { to: "/rentals",   label: "Rentals",      icon: BadgeDollarSign },
      { to: "/sold",      label: "Sold",         icon: ListChecks },
      { to: "/new",       label: "Add Record",   icon: PlusCircle },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/admin/moves",           label: "Drive-Away",       icon: Truck },
      { to: "/admin/drivers-today",   label: "Drivers Today",    icon: CircleCheck },
      { to: "/admin/driver-calendar", label: "Driver Calendar",  icon: CalendarDays },
      { to: "/admin/driver-mgmt",     label: "Driver Mgmt",      icon: UsersIcon },
      { to: "/admin/driver-stats",    label: "Driver Stats",     icon: BarChart3 },
      { to: "/admin/expenses",        label: "Driver Expenses",  icon: Receipt },
      { to: "/admin/driver-documents",label: "Driver Documents", icon: FileText },
      { to: "/admin/trip-cost",       label: "Trip Cost",         icon: MapPin },
    ],
  },
  {
    label: "PDI",
    items: [
      { to: "/pdi",           label: "Start PDI",   icon: ClipboardList },
      { to: "/pdi/manage",    label: "Sessions",    icon: FileText },
      { to: "/pdi/reports",   label: "PDI Reports", icon: BarChart3 },
      { to: "/pdi/templates", label: "Templates",   icon: Layers },
    ],
  },
  {
    label: "Contacts",
    items: [
      { to: "/admin/customers/details", label: "Customers", icon: UserPlus2Icon },
      { to: "/admin/vendors",           label: "Vendors",   icon: LucideTractor },
    ],
  },
  {
    label: "Tools",
    items: [
      { to: "/vin",             label: "Scan VIN",       icon: ScanQrCode },
      { to: "/reports",         label: "Analytics",      icon: BarChart3 },
      { to: "/admin/import",    label: "Import Data",    icon: Upload },
      { to: "/builder-upload",  label: "Builder Upload", icon: Package },
    ],
  },
  {
    label: "Admin",
    items: [{ to: "/admin", label: "Settings", icon: Settings }],
  },
];

// ── helpers ────────────────────────────────────────────────────────────────
function safeParseUser() {
  try { return JSON.parse(sessionStorage.getItem("auth_user") || "null"); }
  catch { return null; }
}
function userLabel(u) {
  if (!u) return "";
  return String(u.display_name || u.email || "").trim();
}

export default function AppSidebar() {
  const nav = useNavigate();
  const loc = useLocation();
  const { theme, setTheme } = useContext(ThemeCtx);

  const [authUser, setAuthUser] = useState(() => safeParseUser());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen]     = useState(false);
  const [msgOpen, setMsgOpen]       = useState(false);

  const userBtnRef = useRef(null);
  const userPopRef = useRef(null);

  // keep user state in sync with auth changes
  useEffect(() => {
    const sync = () => setAuthUser(safeParseUser());
    window.addEventListener("storage", sync);
    window.addEventListener("auth_changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("auth_changed", sync);
    };
  }, []);

  // close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); setUserOpen(false); }, [loc.pathname]);

  // click-outside closes user popover
  useEffect(() => {
    if (!userOpen) return;
    const onDown = (e) => {
      if (userBtnRef.current?.contains(e.target) || userPopRef.current?.contains(e.target)) return;
      setUserOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [userOpen]);

  const displayName = useMemo(() => userLabel(authUser), [authUser]);

  const isActive = (to) => {
    if (to === "/home") return loc.pathname === "/home";
    if (to === "/admin") return loc.pathname === "/admin"; // exact match — sub-paths have their own entries
    return loc.pathname === to || loc.pathname.startsWith(to + "/");
  };

  const go = (to) => { setMobileOpen(false); nav(to); };

  // Theme toggle: cycles light ↔ dark. System is the default if nothing saved.
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  const logout = () => {
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_user");
    window.dispatchEvent(new Event("auth_changed"));
    nav("/", { replace: true });
  };

  return (
    <>
      {/* Mobile topbar — hamburger + brand */}
      <div className="app-topbar-mobile">
        <button className="btn-ghost" onClick={() => setMobileOpen(true)} style={{ padding: 6 }} aria-label="Open menu">
          <Menu size={20} />
        </button>
        <div style={{ fontWeight: 900 }}>SNA IMS</div>
        <button className="btn-ghost" onClick={toggleTheme} style={{ padding: 6 }} aria-label="Toggle theme">
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && <div className="app-sidebar-backdrop" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar itself */}
      <aside className={`app-sidebar${mobileOpen ? " open" : ""}`}>
        {/* Brand */}
        <div className="app-sidebar-brand">
          <div style={{ fontWeight: 900, fontSize: 15, letterSpacing: 0.3 }}>SNA IMS</div>
          <button
            className="btn-ghost"
            onClick={() => setMobileOpen(false)}
            style={{ padding: 4 }}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav groups */}
        <nav className="dash-nav app-sidebar-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: 6 }}>
              <div className="dash-section">{group.label}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to);
                return (
                  <button
                    key={item.to + item.label}
                    className={`dash-navbtn${active ? " active" : ""}`}
                    onClick={() => go(item.to)}
                    style={{ marginTop: 2 }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <Icon size={15} style={{ opacity: 0.75, flexShrink: 0 }} />
                      <span className="dash-navtitle">{item.label}</span>
                    </span>
                    <ChevronRight size={13} style={{ opacity: 0.3 }} />
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom bar: user + theme + logout */}
        <div className="app-sidebar-footer">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {displayName ? (
              <div style={{ position: "relative", flex: 1 }}>
                <button
                  ref={userBtnRef}
                  onClick={() => setUserOpen(v => !v)}
                  title={displayName}
                  className="dash-navbtn"
                  style={{ width: "100%", marginTop: 0 }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                    <UserRoundCog size={15} style={{ opacity: 0.75, flexShrink: 0 }} />
                    <span className="dash-navtitle" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {displayName}
                    </span>
                  </span>
                </button>
                {userOpen && (
                  <div ref={userPopRef} style={{
                    position: "absolute", bottom: "calc(100% + 6px)", left: 0, right: 0,
                    background: "var(--card-bg)", border: "1px solid var(--card-border)",
                    borderRadius: 10, padding: 8, boxShadow: "0 10px 30px rgba(0,0,0,0.25)", zIndex: 50,
                  }}>
                    <button className="dash-navbtn" onClick={() => { setUserOpen(false); setMsgOpen(true); }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <MessagesSquare size={15} style={{ opacity: 0.75 }} />
                        <span className="dash-navtitle">Messages</span>
                      </span>
                    </button>
                    <button className="dash-navbtn" onClick={logout}>
                      <span style={{ display: "flex", alignItems: "center", gap: 9, color: "#dc2626" }}>
                        <LogOut size={15} style={{ opacity: 0.85 }} />
                        <span className="dash-navtitle">Logout</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ) : null}

            <button
              onClick={toggleTheme}
              className="btn-ghost"
              title={`Switch to ${isDark ? "light" : "dark"} mode`}
              style={{
                padding: 8, borderRadius: 8, border: "1px solid var(--card-border)",
                background: "var(--card-bg)", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>
          </div>
        </div>
      </aside>

      {/* Messenger window (floating) */}
      {msgOpen && <MessageWindow open={msgOpen} onClose={() => setMsgOpen(false)} />}
    </>
  );
}
